import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import imageCompression from 'browser-image-compression';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { Upload } from 'lucide-react';

interface ProfilePictureUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: number;
  currentProfileImage?: string | null;
  onSuccess: () => void;
}

interface CroppedArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

async function getCroppedImg(imageSrc: string, pixelCrop: CroppedArea): Promise<Blob> {
  const image = new Image();
  image.src = imageSrc;
  
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas is empty'));
        }
      },
      'image/png'
    );
  });
}

export function ProfilePictureUploadDialog({
  open,
  onOpenChange,
  userId,
  currentProfileImage,
  onSuccess,
}: ProfilePictureUploadDialogProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedArea | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: CroppedArea) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size before processing (20MB limit)
      if (file.size > 20 * 1024 * 1024) {
        toast({
          title: "Error",
          description: "File size must be less than 20MB",
          variant: "destructive"
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      // Validate file size before processing (20MB limit)
      if (file.size > 20 * 1024 * 1024) {
        toast({
          title: "Error",
          description: "File size must be less than 20MB",
          variant: "destructive"
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      toast({
        title: 'Error',
        description: 'Please select and crop an image first',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);

    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

      const resizedBlob = await imageCompression(croppedBlob as File, {
        maxWidthOrHeight: 120,
        useWebWorker: true,
        fileType: 'image/webp',
        initialQuality: 0.8,
      });

      const canvas = document.createElement('canvas');
      canvas.width = 120;
      canvas.height = 120;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      const img = new Image();
      const imgUrl = URL.createObjectURL(resizedBlob);
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imgUrl;
      });

      ctx.drawImage(img, 0, 0, 120, 120);
      URL.revokeObjectURL(imgUrl);

      // Send the image as a binary multipart upload (NOT a base64 string inside a
      // JSON body) — the production edge/WAF aborts requests whose JSON body
      // contains a long base64 `data:` URI with a bare "403 Forbidden".
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/webp', 0.8),
      );
      if (!blob) {
        throw new Error('Failed to encode image');
      }

      const formData = new FormData();
      formData.append('profileImage', blob, 'profile.webp');

      await apiRequest('PATCH', `/api/auth/users/${userId}/profile-picture`, formData);

      toast({
        title: 'Success',
        description: 'Profile picture uploaded successfully',
      });

      onSuccess();
      onOpenChange(false);
      
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload profile picture',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} data-testid="profile-picture-upload-dialog">
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload Profile Picture</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upload an image and select the area for your profile picture (120x120 pixels)
          </p>

          {!imageSrc ? (
            <div
              className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-12 text-center hover:border-muted-foreground/50 transition-colors cursor-pointer"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => document.getElementById('profile-picture-file-input')?.click()}
            >
              <Upload className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-2 text-sm text-muted-foreground">
                Drag and drop an image here, or click to select
              </p>
              <input
                id="profile-picture-file-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
                data-testid="profile-picture-file-input"
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative h-96 bg-muted rounded-lg overflow-hidden">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full"
                  data-testid="zoom-slider"
                />
              </div>

              <Button
                variant="outline"
                onClick={() => setImageSrc(null)}
                className="w-full"
                data-testid="choose-different-image-button"
              >
                Choose Different Image
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isProcessing}
            data-testid="cancel-profile-picture-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!imageSrc || isProcessing}
            data-testid="save-profile-picture-button"
          >
            {isProcessing ? 'Processing...' : 'Save Profile Picture'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
